import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Edit2,
  User,
  Calendar as CalendarIcon,
  DollarSign,
  TrendingUp,
  MapPin,
  CheckCircle,
  PlusCircle,
  Layers,
  Shield,
  Download,
  FileText,
} from "lucide-react";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { MyDomain } from "../../../App";
import BossUpdateEmployeeModal from "../BossEmployeeComps/BossUpdateEmployeeModal";
import BossCreateDayWork from "../BossDailyWorkComps/BossCreateDailyWork";

export default function BossStatisticEmployeeData() {
  const history = useHistory();
  const { employeeId } = useParams();

  const [choosedEmployee, setChoosedEmployee] = useState(null);
  const [allData, setAllData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [choosedMonth, setChoosedMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [existingEmployees, setExistingEmployees] = useState([]);

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    const employee = localStorage.getItem("choosedEmployee");
    const data = localStorage.getItem("allData");
    const user = localStorage.getItem("user");
    const month =
      localStorage.getItem("choosedMonth") || moment().format("MM-YYYY");

    if (employee) setChoosedEmployee(JSON.parse(employee));
    if (data) setAllData(JSON.parse(data));
    if (user) setUserData(JSON.parse(user));
    setChoosedMonth(month);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (choosedMonth) {
      localStorage.setItem("choosedMonth", choosedMonth);
    }
  }, [choosedMonth]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowExportMenu(false);
    if (showExportMenu) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showExportMenu]);

  const handleCreateSuccess = async () => {
    setShowCreateModal(false);
    setExistingEmployees([]);
    setSelectedPlaceId(null);

    if (userData) {
      try {
        const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
          UserId: userData.id,
        });
        setAllData(response.data || {});
        localStorage.setItem("allData", JSON.stringify(response.data));
      } catch (err) {
        console.error("❌ Error fetching data:", err);
      }
    }
  };

  const handleUpdateSuccess = async () => {
    setShowEditModal(false);

    if (userData) {
      try {
        const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
          UserId: userData.id,
        });
        setAllData(response.data || {});
        localStorage.setItem("allData", JSON.stringify(response.data));

        const updatedEmployee = response.data.myEmployees?.find(
          (emp) => emp.id === choosedEmployee.id,
        );
        if (updatedEmployee) {
          setChoosedEmployee(updatedEmployee);
          localStorage.setItem(
            "choosedEmployee",
            JSON.stringify(updatedEmployee),
          );
        }
      } catch (err) {
        console.error("❌ Error fetching data:", err);
      }
    }
  };

  if (loading || !choosedEmployee || !allData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isMonthlyEmployee = choosedEmployee?.typeSalary === 2;
  const employeeSalary = isMonthlyEmployee
    ? choosedEmployee?.monthlySalary || 0
    : choosedEmployee?.salary || 0;

  const generateMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const y = d.getFullYear();
      months.push(`${m}-${y}`);
    }
    return months;
  };

  const monthList = generateMonths();

  const filteredDailyWork = (allData?.myDailyWork || []).filter((item) => {
    if (!choosedEmployee?.id || !choosedMonth) return false;
    const [day, month, year] = item.date.split("-");
    const itemMonth = `${month}-${year}`;
    return item.employeeId === choosedEmployee.id && itemMonth === choosedMonth;
  });

  const groupByDate = () => {
    const grouped = {};
    filteredDailyWork.forEach((item) => {
      if (!grouped[item.date]) grouped[item.date] = [];
      grouped[item.date].push(item);
    });
    return grouped;
  };

  const groupedData = groupByDate();

  const getAllDaysInMonth = () => {
    if (!choosedMonth) return [];
    const [month, year] = choosedMonth.split("-");
    const firstDay = moment(`${year}-${month}-01`, "YYYY-MM-DD");
    const daysInMonth = firstDay.daysInMonth();
    const allDays = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStr = String(day).padStart(2, "0");
      allDays.push(`${dayStr}-${month}-${year}`);
    }
    return allDays;
  };

  const allDaysInMonth = getAllDaysInMonth();

  const getDayTotal = (date) => {
    const dayData = groupedData[date] || [];
    return dayData.reduce(
      (sum, item) => sum + (parseFloat(item.salary) || 0),
      0,
    );
  };

  // Check if a day has notes — new feature from React Native
  const hasNotes = (date) => {
    const dayData = groupedData[date] || [];
    return dayData.some((item) => item.note && item.note.trim() !== "");
  };

  const workingDays = Object.keys(groupedData).length;

  const calculateTotalSalary = () => {
    if (isMonthlyEmployee) {
      return workingDays > 0 ? employeeSalary : 0;
    }
    return filteredDailyWork.reduce(
      (sum, item) => sum + (parseFloat(item.salary) || 0),
      0,
    );
  };

  const totalSalary = calculateTotalSalary();

  const calculateAvgDailySalary = () => {
    if (isMonthlyEmployee) {
      return workingDays > 0 ? employeeSalary / workingDays : 0;
    }
    return workingDays > 0 ? totalSalary / workingDays : 0;
  };

  const avgDailySalary = calculateAvgDailySalary();

  const getPlaceName = (placeId) => {
    const place = allData.myPlaces?.find((p) => p.id === placeId);
    return place?.place || `Place ${placeId}`;
  };

  const getManagerName = (managerId) => {
    const manager = allData.myEmployees?.find((e) => e.id === managerId);
    return manager?.name || "Me";
  };

  const getDayName = (date) => {
    return moment(date, "DD-MM-YYYY").format("ddd");
  };

  const handleDayClick = (date) => {
    const dayData = groupedData[date] || [];

    if (dayData.length > 0) {
      const workEntry = dayData[0];
      const placeId = workEntry.placeId;

      const allEmployeesThisDay = allData.myDailyWork.filter(
        (item) => item.date === date && item.placeId === placeId,
      );

      const existingEmps = allEmployeesThisDay.map((item) => {
        const employee = allData.myEmployees.find(
          (e) => e.id === item.employeeId,
        );
        return {
          id: item.employeeId,
          name: employee?.name || `Employee ${item.employeeId}`,
          salary: item.salary.toString(),
        };
      });

      setSelectedDate(date);
      setSelectedPlaceId(placeId);
      setExistingEmployees(existingEmps);
    } else {
      setSelectedDate(date);
      setSelectedPlaceId(null);
      setExistingEmployees([
        {
          id: choosedEmployee.id,
          name: choosedEmployee.name,
          salary: isMonthlyEmployee ? "0" : employeeSalary.toString(),
        },
      ]);
    }

    setShowCreateModal(true);
  };

  // ─── Export: generate HTML report ────────────────────────────────────────
  const generateReportHTML = () => {
    const monthName = moment(choosedMonth, "MM-YYYY").format("MMMM YYYY");

    let workDaysHTML = "";
    allDaysInMonth.forEach((date) => {
      const dayData = groupedData[date] || [];
      if (dayData.length === 0) return;

      const dayTotal = getDayTotal(date);
      const place = getPlaceName(dayData[0].placeId);
      const manager = dayData[0].whoMake
        ? getManagerName(dayData[0].whoMake)
        : "-";
      const notes =
        dayData
          .map((d) => d.note)
          .filter((n) => n && n.trim() !== "")
          .join(", ") || "-";
      const dayNumber = moment(date, "DD-MM-YYYY").format("DD");
      const dayName = getDayName(date);

      workDaysHTML += `
        <tr>
          <td style="padding:12px;border-bottom:1px solid #E5E7EB">${dayNumber}</td>
          <td style="padding:12px;border-bottom:1px solid #E5E7EB">${dayName}</td>
          <td style="padding:12px;border-bottom:1px solid #E5E7EB">${place}</td>
          <td style="padding:12px;border-bottom:1px solid #E5E7EB">${
            !isMonthlyEmployee ? `₪${dayTotal.toFixed(2)}` : "Worked"
          }</td>
          <td style="padding:12px;border-bottom:1px solid #E5E7EB">${manager}</td>
          <td style="padding:12px;border-bottom:1px solid #E5E7EB;max-width:200px;word-wrap:break-word">${notes}</td>
        </tr>
      `;
    });

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body{font-family:Arial,sans-serif;padding:40px}
    .header{text-align:center;margin-bottom:40px;border-bottom:3px solid #3B82F6;padding-bottom:20px}
    .header h1{color:#3B82F6;margin:0;font-size:32px}
    .header h2{color:#6B7280;margin:10px 0 0;font-size:18px;font-weight:normal}
    .employee-info{background:#F9FAFB;padding:20px;border-radius:12px;margin-bottom:30px;border:1px solid #E5E7EB}
    .info-row{display:flex;justify-content:space-between;margin-bottom:10px}
    .info-label{color:#6B7280;font-weight:600}
    .info-value{color:#111827;font-weight:bold}
    .summary-cards{display:flex;gap:20px;margin-bottom:30px}
    .summary-card{flex:1;background:#fff;border:2px solid #E5E7EB;border-radius:12px;padding:20px;text-align:center}
    .summary-card.blue{border-color:#3B82F6}
    .summary-card.green{border-color:#10B981}
    .summary-card.purple{border-color:#8B5CF6}
    .summary-label{color:#6B7280;font-size:14px;margin-bottom:8px}
    .summary-value{color:#111827;font-size:24px;font-weight:bold}
    .work-table{width:100%;border-collapse:collapse;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,.1)}
    .work-table th{background:#3B82F6;color:#fff;padding:12px;text-align:left;font-weight:600}
    .work-table td{padding:12px;border-bottom:1px solid #E5E7EB}
    .section-title{font-size:20px;font-weight:bold;color:#111827;margin-bottom:16px;padding-bottom:8px;border-bottom:2px solid #E5E7EB}
    .footer{text-align:center;margin-top:40px;padding-top:20px;border-top:1px solid #E5E7EB;color:#6B7280;font-size:12px}
  </style>
</head>
<body>
  <div class="header">
    <h1>Work History</h1>
    <h2>${choosedEmployee?.name} - ${monthName}</h2>
  </div>
  <div class="employee-info">
    <div class="info-row"><span class="info-label">Employee Name:</span><span class="info-value">${choosedEmployee?.name}</span></div>
    <div class="info-row"><span class="info-label">Salary Type:</span><span class="info-value">${isMonthlyEmployee ? "Monthly" : "Daily"}</span></div>
    <div class="info-row"><span class="info-label">${isMonthlyEmployee ? "Monthly Salary" : "Daily Salary"}:</span><span class="info-value">₪${employeeSalary.toLocaleString()}</span></div>
  </div>
  <div class="summary-cards">
    <div class="summary-card blue"><div class="summary-label">Working Days</div><div class="summary-value">${workingDays}</div></div>
    <div class="summary-card green"><div class="summary-label">${isMonthlyEmployee ? "Monthly Salary" : "Total Earned"}</div><div class="summary-value">₪${totalSalary.toLocaleString()}</div></div>
    <div class="summary-card purple"><div class="summary-label">${isMonthlyEmployee ? "Avg Per Day" : "Avg Daily"}</div><div class="summary-value">₪${avgDailySalary.toFixed(0)}</div></div>
  </div>
  <div class="section-title">Work Days</div>
  ${
    workingDays > 0
      ? `<table class="work-table">
           <thead><tr><th>Day</th><th>Day Name</th><th>Place</th><th>Salary</th><th>Manager</th><th>Notes</th></tr></thead>
           <tbody>${workDaysHTML}</tbody>
         </table>`
      : `<p style="text-align:center;color:#6B7280;padding:40px">No work data for this month</p>`
  }
  <div class="footer">Generated on ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
</body>
</html>`;
  };

  // ─── Export as PDF (print dialog) ────────────────────────────────────────
  const exportToPDF = () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      const html = generateReportHTML();
      const printWindow = window.open("", "_blank");
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        setIsExporting(false);
      }, 500);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      setIsExporting(false);
    }
  };

  // ─── Export as Excel (CSV fallback for browser) ───────────────────────
  const exportToExcel = () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      const monthName = moment(choosedMonth, "MM-YYYY").format("MMMM YYYY");
      const rows = [
        ["Work History", ""],
        [`${choosedEmployee?.name} - ${monthName}`, ""],
        ["", ""],
        ["Employee Information", ""],
        ["Employee Name", choosedEmployee?.name],
        ["Salary Type", isMonthlyEmployee ? "Monthly" : "Daily"],
        [
          isMonthlyEmployee ? "Monthly Salary" : "Daily Salary",
          `₪${employeeSalary.toLocaleString()}`,
        ],
        ["", ""],
        ["Summary", ""],
        ["Working Days", workingDays],
        [
          isMonthlyEmployee ? "Monthly Salary" : "Total Earned",
          `₪${totalSalary.toFixed(2)}`,
        ],
        [
          isMonthlyEmployee ? "Avg Per Day" : "Avg Daily",
          `₪${avgDailySalary.toFixed(2)}`,
        ],
        ["", ""],
        ["Work Days", ""],
        ["Day", "Day Name", "Place", "Salary", "Manager", "Notes"],
      ];

      allDaysInMonth.forEach((date) => {
        const dayData = groupedData[date] || [];
        if (dayData.length === 0) return;
        const dayTotal = getDayTotal(date);
        const place = getPlaceName(dayData[0].placeId);
        const manager = dayData[0].whoMake
          ? getManagerName(dayData[0].whoMake)
          : "-";
        const notes =
          dayData
            .map((d) => d.note)
            .filter((n) => n && n.trim() !== "")
            .join(", ") || "-";

        rows.push([
          moment(date, "DD-MM-YYYY").format("DD"),
          getDayName(date),
          place,
          !isMonthlyEmployee ? `₪${dayTotal.toFixed(2)}` : "Worked",
          manager,
          notes,
        ]);
      });

      const csvContent =
        "data:text/csv;charset=utf-8," +
        rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");

      const link = document.createElement("a");
      link.setAttribute("href", encodeURI(csvContent));
      link.setAttribute(
        "download",
        `${choosedEmployee?.name}_${choosedMonth}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting Excel:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 pt-8 pb-6 rounded-b-3xl shadow-md">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => history.goBack()}
            className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
          >
            <ArrowLeft className="w-5 h-5 text-gray-900" />
          </button>

          {/* Right actions: Download + Edit */}
          <div className="flex items-center gap-2">
            {/* Export button with dropdown */}
            <div className="relative">
              <button
                className="w-10 h-10 bg-green-50 hover:bg-green-100 rounded-full flex items-center justify-center transition border border-green-200"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowExportMenu((prev) => !prev);
                }}
                disabled={isExporting}
                title="Export Report"
              >
                <Download
                  className={`w-5 h-5 text-green-600 ${isExporting ? "opacity-50" : ""}`}
                />
              </button>

              {showExportMenu && (
                <div
                  className="absolute right-0 top-12 bg-white rounded-xl shadow-lg border border-gray-100 z-50 min-w-[160px] overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={exportToPDF}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <FileText className="w-4 h-4 text-red-500" />
                    Export as PDF
                  </button>
                  <div className="h-px bg-gray-100" />
                  <button
                    onClick={exportToExcel}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <FileText className="w-4 h-4 text-green-500" />
                    Export as CSV
                  </button>
                </div>
              )}
            </div>

            {/* Edit button */}
            <button
              className="w-10 h-10 bg-purple-50 hover:bg-purple-100 rounded-full flex items-center justify-center transition"
              onClick={() => setShowEditModal(true)}
            >
              <Edit2 className="w-5 h-5 text-purple-600" />
            </button>
          </div>
        </div>

        {/* Employee Info */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              {choosedEmployee?.name}
            </h1>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Employee Statistics</span>
              <div
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold ${
                  isMonthlyEmployee
                    ? "bg-purple-100 text-purple-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                <CalendarIcon className="w-3 h-3" />
                <span>{isMonthlyEmployee ? "Monthly" : "Daily"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Base Salary Info */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {isMonthlyEmployee
                  ? "Base Monthly Salary"
                  : "Base Daily Salary"}
              </span>
            </div>
            <span className="text-lg font-bold text-gray-900">
              ₪{employeeSalary.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 space-y-6 pb-24">
        {/* Month Selector */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4">Select Month</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
            {monthList.map((item) => {
              const [month, year] = item.split("-");
              const monthName = moment(item, "MM-YYYY").format("MMM");
              const isSelected = choosedMonth === item;

              return (
                <button
                  key={item}
                  onClick={() => setChoosedMonth(item)}
                  className={`flex-shrink-0 px-5 py-3 rounded-2xl border-2 min-w-[90px] transition-all ${
                    isSelected
                      ? "bg-blue-600 border-blue-600 text-white shadow-lg"
                      : "bg-white border-gray-200 text-gray-700 hover:border-blue-300"
                  }`}
                >
                  <div className="text-center">
                    <p
                      className={`text-base font-semibold ${
                        isSelected ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {monthName}
                    </p>
                    <p
                      className={`text-xs ${
                        isSelected ? "text-blue-100" : "text-gray-500"
                      }`}
                    >
                      {year}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Summary Cards — compact 3-column row (matched from React Native) */}
        <div className="flex gap-3">
          {/* Working Days */}
          <div className="flex-1 bg-white rounded-2xl p-3 shadow-sm border border-blue-100 border-b-4 border-b-blue-500 flex flex-col items-center">
            <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-sm mb-2">
              <CalendarIcon className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-lg font-bold text-gray-900 mb-0.5">
              {workingDays}
            </p>
            <p className="text-[10px] text-gray-500 font-medium text-center">
              Working Days
            </p>
          </div>

          {/* Total Salary */}
          <div className="flex-1 bg-white rounded-2xl p-3 shadow-sm border border-green-100 border-b-4 border-b-green-500 flex flex-col items-center">
            <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-sm mb-2">
              <DollarSign className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-lg font-bold text-gray-900 mb-0.5">
              ₪{totalSalary.toLocaleString()}
            </p>
            <p className="text-[10px] text-gray-500 font-medium text-center">
              {isMonthlyEmployee ? "Monthly Salary" : "Total Earned"}
            </p>
          </div>

          {/* Average */}
          <div className="flex-1 bg-white rounded-2xl p-3 shadow-sm border border-purple-100 border-b-4 border-b-purple-500 flex flex-col items-center">
            <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-sm mb-2">
              <TrendingUp className="w-4 h-4 text-purple-500" />
            </div>
            <p className="text-lg font-bold text-gray-900 mb-0.5">
              ₪{avgDailySalary.toFixed(0)}
            </p>
            <p className="text-[10px] text-gray-500 font-medium text-center">
              {isMonthlyEmployee ? "Avg Per Day" : "Avg Daily"}
            </p>
          </div>
        </div>

        {/* Monthly calculation info (shown only for monthly employees) */}
        {isMonthlyEmployee && workingDays > 0 && (
          <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-xl px-3 py-2.5">
            <TrendingUp className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <p className="text-xs font-semibold text-purple-700">
              ₪{employeeSalary.toLocaleString()} ÷ {workingDays} days = ₪
              {avgDailySalary.toFixed(2)}
            </p>
          </div>
        )}

        {/* Work History */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Work History</h2>
            {choosedMonth && (
              <div className="bg-blue-50 px-4 py-2 rounded-xl">
                <span className="text-sm font-semibold text-blue-600">
                  {moment(choosedMonth, "MM-YYYY").format("MMMM YYYY")}
                </span>
              </div>
            )}
          </div>

          {allDaysInMonth.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CalendarIcon className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Month Selected
              </h3>
              <p className="text-sm text-gray-500">
                Select a month above to view work history
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-2">
              {allDaysInMonth.map((date) => {
                const dayData = groupedData[date] || [];
                const dayTotal = getDayTotal(date);
                const hasWork = dayData.length > 0;
                const hasNote = hasNotes(date);
                const isToday = moment(date, "DD-MM-YYYY").isSame(
                  moment(),
                  "day",
                );
                const dayNumber = moment(date, "DD-MM-YYYY").format("DD");
                const dayName = getDayName(date);

                return (
                  <button
                    key={date}
                    onClick={() => handleDayClick(date)}
                    className={`relative bg-white rounded-xl p-3 min-h-[100px] transition-all border-2 ${
                      hasWork
                        ? "border-green-500 hover:shadow-md"
                        : "border-gray-200 hover:border-gray-300"
                    } ${isToday ? "ring-2 ring-blue-500 border-blue-500" : ""}`}
                  >
                    {/* Note indicator — new feature from React Native */}
                    {hasNote && (
                      <div className="absolute bottom-1 left-1 w-4 h-4 bg-yellow-100 rounded-full flex items-center justify-center z-10">
                        <FileText className="w-2.5 h-2.5 text-yellow-500" />
                      </div>
                    )}

                    {/* Date Header */}
                    <div className="flex justify-between items-start mb-2 pb-2 border-b border-gray-100">
                      <span
                        className={`text-lg font-bold ${
                          hasWork
                            ? "text-green-600"
                            : isToday
                              ? "text-blue-600"
                              : "text-gray-400"
                        }`}
                      >
                        {dayNumber}
                      </span>
                      <span
                        className={`text-[10px] font-semibold uppercase ${
                          hasWork ? "text-gray-600" : "text-gray-400"
                        }`}
                      >
                        {dayName}
                      </span>
                    </div>

                    {/* Content */}
                    {hasWork ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-green-600 flex-shrink-0" />
                          <span className="text-[10px] font-semibold text-gray-900 truncate">
                            {getPlaceName(dayData[0].placeId)}
                          </span>
                        </div>

                        {!isMonthlyEmployee && (
                          <div className="bg-rose-50 px-2 py-1 rounded">
                            <span className="text-xs font-bold text-rose-600">
                              ₪{dayTotal.toFixed(0)}
                            </span>
                          </div>
                        )}

                        {isMonthlyEmployee && (
                          <div className="flex items-center gap-1 bg-purple-50 px-2 py-1 rounded">
                            <CheckCircle className="w-3 h-3 text-purple-600" />
                            <span className="text-[10px] font-semibold text-purple-600">
                              Worked
                            </span>
                          </div>
                        )}

                        {dayData.length > 1 && (
                          <div className="flex items-center gap-1">
                            <Layers className="w-3 h-3 text-gray-600" />
                            <span className="text-[10px] font-semibold text-gray-600">
                              {dayData.length}
                            </span>
                          </div>
                        )}

                        {dayData[0].whoMake && (
                          <div className="flex items-center gap-1 bg-purple-50 px-1 py-0.5 rounded">
                            <Shield className="w-2 h-2 text-purple-600" />
                            <span className="text-[8px] font-semibold text-purple-600 truncate">
                              {getManagerName(dayData[0].whoMake)}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-2">
                        <PlusCircle className="w-7 h-7 text-gray-300 mb-1" />
                        <span className="text-[10px] font-medium text-gray-400">
                          No Work
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Day Work Modal */}
      <BossCreateDayWork
        visible={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setExistingEmployees([]);
          setSelectedPlaceId(null);
        }}
        onSuccess={handleCreateSuccess}
        places={allData.myPlaces}
        employees={allData.myEmployees}
        preselectedDate={selectedDate}
        preselectedPlaceId={selectedPlaceId}
        preselectedEmployees={existingEmployees}
      />

      {/* Update Employee Modal */}
      <BossUpdateEmployeeModal
        visible={showEditModal}
        onClose={() => setShowEditModal(false)}
        employee={choosedEmployee}
        onUpdateSuccess={handleUpdateSuccess}
      />

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
